import React from 'react'
import { Table, Spinner, Badge } from 'react-bootstrap'
import { SmartContract } from '../types'
import * as S from '../styles'

interface SmartContractTableProps {
  contracts: SmartContract[]
  loading: boolean
}

export const SmartContractTable: React.FC<SmartContractTableProps> = ({
  contracts,
  loading
}) => {
  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(date))
  }

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('pt-BR').format(value)
  }

  const getTypeLabel = (type: string) => {
    const types: Record<string, string> = {
      ContractsManager: 'Gerenciador de Contratos',
      ReceivablesManager: 'Gerenciador de Recebíveis',
      GovernanceManager: 'Gerenciador de Governança',
      PermissionsManager: 'Gerenciador de Permissões'
    }
    return types[type] || type
  }

  const getTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      ContractsManager: 'primary',
      ReceivablesManager: 'success',
      GovernanceManager: 'warning',
      PermissionsManager: 'info'
    }
    return colors[type] || 'secondary'
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    // Could add a toast notification here
  }

  if (loading) {
    return (
      <S.LoadingSpinner>
        <Spinner animation="border" />
      </S.LoadingSpinner>
    )
  }

  if (contracts.length === 0) {
    return (
      <div className="text-center py-4">
        <p className="text-muted">Nenhum smart contract encontrado</p>
      </div>
    )
  }

  return (
    <S.TableContainer>
      <Table responsive hover>
        <thead>
          <tr>
            <th>Tipo de Contrato</th>
            <th>Endereço na Blockchain</th>
            <th>Versão</th>
            <th>Empresa</th>
            <th>Data de Criação</th>
            <th>Transações</th>
          </tr>
        </thead>
        <tbody>
          {contracts.map((contract) => (
            <tr key={contract.id}>
              <td>
                <div className="d-flex align-items-center">
                  <Badge variant={getTypeColor(contract.type)} className="mr-2">
                    {contract.type}
                  </Badge>
                  <div>
                    <strong>{getTypeLabel(contract.type)}</strong>
                  </div>
                </div>
              </td>
              <td>
                <S.TransactionHash
                  onClick={() => copyToClipboard(contract.blockchainAddress)}
                  title="Clique para copiar o endereço completo"
                >
                  {contract.blockchainAddress.substring(0, 12)}...
                  {contract.blockchainAddress.substring(
                    contract.blockchainAddress.length - 8
                  )}
                </S.TransactionHash>
              </td>
              <td>
                <Badge variant="light">{contract.version}</Badge>
              </td>
              <td>
                <span className="font-weight-medium">
                  {contract.companyName}
                </span>
              </td>
              <td>
                <span>{formatDate(contract.createdAt)}</span>
              </td>
              <td>
                <div className="text-center">
                  <span className="font-weight-bold text-primary">
                    {formatNumber(contract.transactionCount)}
                  </span>
                  <br />
                  <small className="text-muted">transações</small>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <div className="mt-4 p-3 bg-light rounded">
        <h6 className="mb-3">Descrição dos Smart Contracts:</h6>
        <div className="row">
          <div className="col-md-6">
            <div className="mb-2">
              <Badge variant="primary" className="mr-2">
                ContractsManager
              </Badge>
              <small>
                Gerencia o registro e status de todos os contratos na blockchain
              </small>
            </div>
            <div className="mb-2">
              <Badge variant="success" className="mr-2">
                ReceivablesManager
              </Badge>
              <small>Controla os recebíveis e pagamentos dos contratos</small>
            </div>
          </div>
          <div className="col-md-6">
            <div className="mb-2">
              <Badge variant="warning" className="mr-2">
                GovernanceManager
              </Badge>
              <small>Sistema de governança e votação da plataforma</small>
            </div>
            <div className="mb-2">
              <Badge variant="info" className="mr-2">
                PermissionsManager
              </Badge>
              <small>Gerencia permissões e controle de acesso</small>
            </div>
          </div>
        </div>
      </div>
    </S.TableContainer>
  )
}
